import React, { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import SearchableSelect from '../../ui/searchable-select';
import { PractitionerSelectionProps } from './types';
import { PaginationParams, Practitioner } from '@/lib/types';
import { practitionerService } from '@/services/practitionerService';
import { useClinic } from '@/contexts/ClinicContext';
import { useToast } from '@/hooks/use-toast';

const PractitionerSelection: React.FC<PractitionerSelectionProps> = ({
  selectedPractitionerId,
  onPractitionerSelect,
  isLoading,
  error,
}) => {
  const { clinic } = useClinic();
  const { toast } = useToast();
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 100,
    totalItems: 0,
    totalPages: 0,
    search: '',
    sortBy: 'full_name',
    sortOrder: 'ASC',
  });

  useEffect(() => {
    practitionerService
      .getPractitionerDropdownByClinic(clinic?.id, {
        page: pagination.page,
        limit: pagination.limit,
        search: pagination.search,
        sortBy: pagination.sortBy,
        sortOrder: pagination.sortOrder,
      })
      .then(result => {
        setPractitioners(result?.data?.practitioners || []);
      })
      .catch(error => {
        console.error('Error fetching practitioners:', error);
        toast({
          title: 'Error fetching practitioners',
          description: 'Please try again later',
          variant: 'destructive',
        });
      });
  }, [
    pagination.search,
    pagination.page,
    pagination.limit,
    pagination.sortBy,
    pagination.sortOrder,
  ]);
  return (
    <div className='mb-6 space-y-4'>
      <h3 className='text-[0.8rem] font-semibold uppercase text-[#60378B]'>
        Practitioner Information
      </h3>
      <div className='space-y-1.5' data-field="practitioner_identifier">
        <Label htmlFor='practitioner'>
          Practitioner <span className='text-red-500'>*</span>
        </Label>
        {practitioners.length > 0 ? (
          <SearchableSelect
            options={practitioners.map(practitioner => ({
              ...practitioner,
              type: 'practitioner',
            }))}
            value={practitioners.find(p => p.id === selectedPractitionerId) || null}
            onSelect={option => {
              onPractitionerSelect(option as Practitioner);
            }}
            onSearch={query => {
              setPagination(prev => ({ ...prev, search: query, page: 1 }));
            }}
            placeholder='Select a practitioner...'
            searchPlaceholder='Search practitioners by name or specialty...'
            type='practitioner'
            loading={isLoading}
            error={error}
            disabled={isLoading}
          />
        ) : (
          <p className='text-[0.8rem] text-gray-500'>No practitioners found</p>
        )}
      </div>
    </div>
  );
};

export default PractitionerSelection;
